import { useEffect } from "react";
import { useGlobalSearchParams, useSegments } from "expo-router";
import { setTags } from "@visioni/a3s-error-reporting";

/** A param repeated in the query string arrives as an array; its first value is the one that counts. */
const firstValue = (value: string | string[] | undefined): string | null =>
  (typeof value === "string" ? value : value?.[0]) ?? null;

/**
 * Tags errors with `screen`, `site_id` and `camera_key`, named as in analytics
 * (`TenantProvider` tags the tenant). Call once, in the root layout.
 *
 * `screen` is the route template (`/tenants/[tenantId]`), never the URL, so no ids
 * reach it. Site may be a path or a query param; both arrive in the params.
 *
 * These hooks update once a navigation settles, so an error thrown during a new
 * screen's first render can still carry the previous screen's tags.
 */
export function useRouteErrorContext(): void {
  const segments = useSegments();
  const params = useGlobalSearchParams();
  const screen = `/${segments.filter((segment) => !(segment.startsWith("(") && segment.endsWith(")"))).join("/")}`;
  const siteId = firstValue(params["siteId"]);
  const cameraKey = firstValue(params["cameraKey"]);

  useEffect(() => {
    setTags({ screen, site_id: siteId, camera_key: cameraKey });
  }, [screen, siteId, cameraKey]);
}
